// src/components/Dashboard/Dashboard.js

import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Avatar,
  Button,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Slider,
  Stack,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Alert,
} from '@mui/material';
import { CopyAll as CopyIcon, Visibility, VisibilityOff } from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import API from '../config';

export default function Dashboard() {
  const { authData } = useContext(AuthContext);
  const token = authData?.access_token;
  const navigate = useNavigate();

  // State
  const [profile, setProfile] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [referralStats, setReferralStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [editProfile, setEditProfile] = useState({});
  const [profileImage, setProfileImage] = useState(null);

  const [showSubscriptionList, setShowSubscriptionList] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [reviewCreator, setReviewCreator] = useState(null);
  const [refreshingSubscriptions, setRefreshingSubscriptions] = useState(false);
  const [showRefreshCredentialsModal, setShowRefreshCredentialsModal] = useState(false);
  const [refreshCredentials, setRefreshCredentials] = useState({
    email: '',
    password: ''
  });
  const [showRefreshPassword, setShowRefreshPassword] = useState(false);
  const [refreshingWithCredentials, setRefreshingWithCredentials] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    recommend: 'yes',
    spent: '',
    contentQuality: 3,
    contentFrequency: 3,
    authenticity: 3,
    consistency: 3,
    engagement: 3,
    fullReview: '',
  });

  // Redirect if not logged in
  useEffect(() => {
    if (!token) navigate('/login');
  }, [token, navigate]);

  // Fetch dashboard data
  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/api/dashboard`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        setProfile(data.profile);
        setSubscription(data.subscription);
        setEditProfile(data.profile);
      })
      .catch(() => setError('Failed to load dashboard data.'))
      .finally(() => setLoading(false));
  }, [token]);

  // Fetch referral stats
  useEffect(() => {
    if (!profile?.username || !token) return;
    axios
      .get(`${API}/api/creators/username/${profile.username}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => setReferralStats(data))
      .catch(console.error);
  }, [profile, token]);

  const copyToClipboard = (txt, msg) =>
    navigator.clipboard.writeText(txt)
      .then(() => toast.success(msg))
      .catch(() => toast.error('Copy failed'));

  // Profile edit handlers
  const handleEditToggle = () => {
    if (editing && profile) setEditProfile(profile);
    setEditing(e => !e);
  };
  const handleEditChange = e => {
    const { name, value } = e.target;
    setEditProfile(p => ({ ...p, [name]: value }));
  };
  const handleFileChange = e => {
    const { files } = e.target;
    if (files[0]) setProfileImage(files[0]);
  };
  const handleEditSubmit = async e => {
    e.preventDefault();
    const fd = new FormData();
    ['username', 'bio', 'birthdate', 'ofLink'].forEach(k => fd.append(k, editProfile[k]));
    if (profileImage) fd.append('profilePicture', profileImage);
    try {
      const { data } = await axios.put(`${API}/api/dashboard/profile`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setProfile(data.profile);
      setEditing(false);
    } catch {
      setError('Failed to update profile.');
    }
  };

  // Review handlers
  const openReview = creator => {
    setReviewCreator(creator);
    setReviewForm({
      recommend: 'yes',
      spent: '',
      contentQuality: 3,
      contentFrequency: 3,
      authenticity: 3,
      consistency: 3,
      engagement: 3,
      fullReview: '',
    });
    setShowReview(true);
  };
  const handleReviewSubmit = async e => {
    e.preventDefault();
    if (!token || !reviewCreator) return toast.error('Please select a creator to review.');
    const payload = { creator_id: reviewCreator._id, ...reviewForm };
    try {
      await axios.post(`${API}/api/reviews`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Review submitted!');
      setShowReview(false);
    } catch {
      toast.error('Failed to submit review.');
    }
  };

  // Refresh subscriptions handlers
  const handleRefreshSubscriptions = () => {
    if (!token) return;
    // Open the credentials modal instead of directly refreshing
    setShowRefreshCredentialsModal(true);
  };

  const handleRefreshCredentialsChange = e => {
    const { name, value } = e.target;
    setRefreshCredentials(prev => ({ ...prev, [name]: value }));
  };

  const toggleRefreshPasswordVisibility = () => {
    setShowRefreshPassword(prev => !prev);
  };

  const handleRefreshWithCredentials = async e => {
    e.preventDefault();
    if (!token) return;
    
    // Validate inputs
    if (!refreshCredentials.email || !refreshCredentials.password) {
      toast.error('Please enter both email and password');
      return;
    }
    
    setRefreshingWithCredentials(true);
    try {
      await axios.post(`${API}/api/subscriptions/refresh_subscriptions`, 
        refreshCredentials, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Subscriptions refreshed successfully!');
      // Reset form and close modal
      setRefreshCredentials({ email: '', password: '' });
      setShowRefreshCredentialsModal(false);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to refresh subscriptions.');
    } finally {
      setRefreshingWithCredentials(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display:'flex', justifyContent:'center', alignItems:'center', height:'70vh' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ display:'flex', justifyContent:'center', alignItems:'center', height:'70vh', px:2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const origin = window.location.origin;
  const referralLink = profile ? `${origin}?ref=${profile._id}` : '';
  const activeSubsCount = profile?.subscriptions?.length || 0;

  return (
    <Container maxWidth="md" sx={{ py:8 }}>
      {/* Profile Card */}
      <Card sx={{ position:'relative', pt:8 }}>
        <Avatar
          src={profile.profilePicture}
          alt="Avatar"
          sx={{
            width: 96, height:96,
            position:'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            border: '3px solid white',
            bgcolor:'grey.100'
          }}
        />
        <CardContent sx={{ mt:4, textAlign:'center' }}>
          {!editing ? (
            <>
              <Typography variant="h5">{profile.username}</Typography>
              <Typography color="text.secondary" paragraph>
                {profile.bio}
              </Typography>
              <Stack
                direction={{ xs:'column', sm:'row' }}
                spacing={1}
                justifyContent="center"
              >
                <Button variant="contained" onClick={handleEditToggle}>Edit Profile</Button>
                <Button variant="outlined" onClick={() => setShowSubscriptionList(true)}>
                  Subscriptions
                </Button>
                <Button variant="outlined" component={RouterLink} to="/subscription">
                  My Account
                </Button>
              </Stack>
            </>
          ) : (
            <Box
              component="form"
              onSubmit={handleEditSubmit}
              sx={{
                display:'grid',
                gap:1,
                mt:2
              }}
            >
              <TextField label="Username" name="username" value={editProfile.username}
                onChange={handleEditChange} fullWidth />
              <TextField label="Bio" name="bio" value={editProfile.bio}
                onChange={handleEditChange} fullWidth />
              <TextField label="Birthdate" name="birthdate" value={editProfile.birthdate}
                onChange={handleEditChange} type="date" InputLabelProps={{ shrink:true }} fullWidth />
              <Button component="label" fullWidth>
                Upload Avatar
                <input hidden type="file" onChange={handleFileChange} />
              </Button>
              <TextField label="OnlyFans Link" name="ofLink" value={editProfile.ofLink}
                onChange={handleEditChange} fullWidth />
              <Stack direction="row" spacing={1} sx={{ mt:1 }}>
                <Button type="submit" variant="contained" fullWidth>Save</Button>
                <Button variant="outlined" onClick={handleEditToggle} fullWidth>
                  Cancel
                </Button>
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>

      <Dialog
  open={showReview}
  onClose={() => setShowReview(false)}
  fullWidth
  maxWidth="md" // Wider dialog
>
  <DialogTitle>
    <Stack direction="row" spacing={2} alignItems="center">
      <Avatar
        src={reviewCreator?.avatar || reviewCreator?.profilePicture || '/default-profile.png'}
        alt={reviewCreator?.creatorName || reviewCreator?.username || 'Creator'}
        sx={{ width: 48, height: 48 }}
      />
      <Typography variant="h6" component="div" noWrap>
        Leave a Review for {reviewCreator?.creatorName || reviewCreator?.username}
      </Typography>
    </Stack>
  </DialogTitle>

  <DialogContent>
    <Stack spacing={3} sx={{ mt: 1 }}>
      {/* Recommend */}
      <Box>
        <Typography variant="body1" gutterBottom>
          Would you recommend this creator?
        </Typography>
        <ToggleButtonGroup
          value={reviewForm.recommend}
          exclusive
          onChange={(e, val) => val && setReviewForm(f => ({ ...f, recommend: val }))}
          fullWidth
        >
          <ToggleButton value="yes">Yes</ToggleButton>
          <ToggleButton value="no">No</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Questions */}
      {[
        {
          key: 'contentQuality',
          label: '1. Content Quality',
          helper: 'How good is the content overall?',
        },
        {
          key: 'contentFrequency',
          label: '2. Content Frequency',
          helper: 'How often is this creator posting new content?',
          marks: [
            { value: 1, label: 'Rarely' },
            { value: 5, label: 'Frequently' },
          ],
        },
        {
          key: 'authenticity',
          label: '3. Authenticity and Transparency',
          helper: 'Did you get what was advertised?',
          marks: [
            { value: 1, label: 'Misleading' },
            { value: 5, label: 'Honest & Clear' },
          ],
        },
        {
          key: 'consistency',
          label: '4. Consistency and Reliability',
          helper: 'Has this creator delivered consistently?',
          marks: [
            { value: 1, label: 'Inconsistent' },
            { value: 5, label: 'Reliable' },
          ],
        },
        {
          key: 'engagement',
          label: '5. Engagement',
          helper: 'Does this creator interact with their audience?',
        },
      ].map(({ key, label, helper, marks }) => (
        <Box key={key}>
          <Typography variant="body1" gutterBottom>{label}</Typography>
          <Typography variant="body2" gutterBottom>{helper}</Typography>
          <Box sx={{ px: 4 }}>
          <Slider
            value={reviewForm[key]}
            min={1}
            max={5}
            step={1}
            marks={marks || true}
            valueLabelDisplay="auto"
            onChange={(e, val) =>
              setReviewForm(f => ({ ...f, [key]: val }))
            }
          />
          </Box>
        </Box>
      ))}

      {/* Spending */}
      <Box>
        <Typography variant="body1" gutterBottom>
          Roughly how much have you spent on this creator?
        </Typography>
        <Select
          value={reviewForm.spent}
          onChange={e =>
            setReviewForm(f => ({ ...f, spent: e.target.value }))
          }
          fullWidth
          displayEmpty
        >
          <MenuItem value=""><em>Select amount</em></MenuItem>
          {['$0–25','$25–50','$50–100','$100–250','$250+'].map((opt) => (
            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
          ))}
        </Select>
      </Box>

      {/* Full Review */}
      <Box>
        <Typography variant="body1" gutterBottom>
          Optional Full Review
        </Typography>
        <TextField
          placeholder="Write anything you'd like to share..."
          multiline
          rows={4}
          value={reviewForm.fullReview}
          onChange={e => setReviewForm(f => ({ ...f, fullReview: e.target.value }))}
          fullWidth
        />
      </Box>
    </Stack>
  </DialogContent>

  <DialogActions>
    <Button onClick={() => setShowReview(false)}>Cancel</Button>
    <Button variant="contained" onClick={handleReviewSubmit}>
      Submit Review
    </Button>
  </DialogActions>
</Dialog>

      {/* Subscription List Dialog */}
      <Dialog
        open={showSubscriptionList}
        onClose={() => setShowSubscriptionList(false)}
        fullWidth maxWidth="sm"
      >
        <DialogTitle>Your Subscriptions</DialogTitle>
        <DialogContent>
          {profile.subscriptions?.length ? (
            <Stack spacing={2}>
              {profile.subscriptions.map((sub,i) => (
                <Card key={i} variant="outlined">
                  <CardContent sx={{ display:'flex', alignItems:'center', gap:2 }}>
                    <Avatar src={sub.avatar} sx={{ width:40, height:40 }} />
                    <Box flex={1}>
                      <Typography>{sub.creatorName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {sub.name}
                      </Typography>
                    </Box>
                    {!sub.reviewed ? (
                      <Button onClick={() => openReview(sub.creator)}>Review</Button>
                    ) : (
                      <Typography color="success.main">Reviewed</Typography>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Stack>
          ) : (
            <Typography>You have no subscriptions.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            variant="outlined" 
            onClick={handleRefreshSubscriptions}
            disabled={refreshingSubscriptions}
          >
            {refreshingSubscriptions ? <CircularProgress size={20} /> : 'Refresh'}
          </Button>
          <Button onClick={() => setShowSubscriptionList(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Refresh Credentials Modal */}
      <Dialog
        open={showRefreshCredentialsModal}
        onClose={() => setShowRefreshCredentialsModal(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Refresh Subscriptions</DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2 }}
          >
            Must use the same credentials as your OnlyFans account. We encrypt your information—
            we never access or store your OF password.
          </Typography>
          
          <Box component="form" onSubmit={handleRefreshWithCredentials} sx={{ mt: 2 }}>
            <Stack spacing={2}>
              <TextField
                label="Email"
                name="email"
                type="email"
                value={refreshCredentials.email}
                onChange={handleRefreshCredentialsChange}
                required
                fullWidth
              />
              
              <TextField
                label="Password"
                name="password"
                type={showRefreshPassword ? 'text' : 'password'}
                value={refreshCredentials.password}
                onChange={handleRefreshCredentialsChange}
                required
                fullWidth
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={toggleRefreshPasswordVisibility}
                      edge="end"
                    >
                      {showRefreshPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  )
                }}
              />
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRefreshCredentialsModal(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRefreshWithCredentials}
            disabled={refreshingWithCredentials}
          >
            {refreshingWithCredentials ? <CircularProgress size={20} /> : 'Refresh'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Referral Panel */}
      <Card sx={{ mt:4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Referral Dashboard</Typography>
          <Divider sx={{ my:2 }} />
          <Grid container spacing={2} textAlign="center">
            <Grid item xs={4}>
              <Typography variant="h5">{referralStats?.totalReferrals}</Typography>
              <Typography>Total Referrals</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="h5">${referralStats?.earnings}</Typography>
              <Typography>Earnings</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="h5">{activeSubsCount}</Typography>
              <Typography>Active Subs</Typography>
            </Grid>
          </Grid>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt:2 }}>
            <TextField fullWidth value={referralLink} InputProps={{ readOnly:true }} />
            <IconButton onClick={() => copyToClipboard(referralLink,'Copied!')}>
              <CopyIcon />
            </IconButton>
          </Stack>
        </CardContent>
      </Card>
    </Container>
  );
}
